import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { toast } from "sonner";
import { MessageCircle, RefreshCw } from "lucide-react";

// Template health is TabOn's concern, not the venue's (J-2b): a venue admin cannot register, appeal
// or configure a Meta template, so this lives in the superadmin portal — where the person who can
// actually fix it works. Read-only view of the daily preflight (I-5a).
const STATUS_META = {
  active:         { label: "Active",         cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  missing:        { label: "Missing",        cls: "bg-red-500/15 text-red-300 border-red-500/40" },
  wrong_language: { label: "Wrong language", cls: "bg-red-500/15 text-red-300 border-red-500/40" },
  rejected:       { label: "Rejected",       cls: "bg-red-500/15 text-red-300 border-red-500/40" },
  disabled:       { label: "Disabled",       cls: "bg-red-500/15 text-red-300 border-red-500/40" },
  paused:         { label: "Paused",         cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  pending:        { label: "Pending review", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
};
function StatusChip({ status }) {
  const s = STATUS_META[status] || { label: status || "unknown", cls: "bg-white/10 text-[#A0A0A0] border-white/20" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${s.cls}`}>{s.label}</span>;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function SATemplateHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      // Reload forces a fresh Meta sweep (refresh=1) so a just-approved template clears immediately;
      // the initial load just reads the cached daily sweep (K-6a).
      const { data } = await api.get("/admin/whatsapp/template-health", refresh ? { params: { refresh: 1 } } : {});
      setData(data);
      if (refresh && data.throttled) toast.message("Checked moments ago — showing the latest result.");
      else if (refresh && data.refreshed) toast.success("Re-checked against Meta.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const rows = data?.templates || [];

  return (
    <SALayout>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <MessageCircle size={20} className="text-[#9D00FF]" />
          <h1 className="text-2xl font-bold">WhatsApp Template Health</h1>
        </div>
        <button
          onClick={() => load(true)}
          data-testid="sa-template-health-refresh"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 text-xs transition"
        >
          <RefreshCw size={12} /> Reload
        </button>
      </div>
      <p className="text-xs text-[#A0A0A0] mb-5">
        Every template the code can send, checked against the Meta account daily. Venues never see this —
        they cannot register, appeal or configure a template. Fix problems here.
      </p>

      {!loading && !data?.waba_configured && (
        <div className="mb-4 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          WABA id not configured — set <span className="font-mono">META_WHATSAPP_ACCOUNT_ID</span> (or the
          global WhatsApp config). Templates cannot be checked until it is set.
        </div>
      )}
      {!loading && data?.waba_configured && data?.error && (
        <div className="mb-4 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
          Last check couldn't reach Meta ({data.error}). Showing the last known result.
        </div>
      )}

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 text-xs text-[#A0A0A0]">
          <span>{data ? `${data.needs_attention} of ${data.total} need attention` : ""}</span>
          <span>Last checked: {fmtTime(data?.checked_at)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#A0A0A0] border-b border-white/5">
                <th className="px-4 py-3">Template</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-[#A0A0A0]">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-[#A0A0A0]">No template data yet — the daily check has not run.</td></tr>
              )}
              {rows.map((t) => (
                <tr key={t.name} data-testid={`sa-template-${t.name}`} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono text-[#D0D0D0]">{t.name}</td>
                  <td className="px-4 py-3"><StatusChip status={t.status} /></td>
                  <td className="px-4 py-3 text-[#A0A0A0] max-w-[420px]">{t.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SALayout>
  );
}
